import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { maintenanceApi } from '@/api/maintenanceApi'
import { MaintenanceNotice, MaintenanceState } from '@/models/MaintenanceNotice'

const POLL_INTERVAL_MS = 5 * 60 * 1000
const DISMISSED_KEY_PREFIX = 'maintenance-dismissed:'

export const useMaintenanceStore = defineStore('maintenance', () => {

    const notice = ref<MaintenanceNotice | null>(null)
    const language = ref('en')
    const dismissedIds = ref<string[]>([])

    let pollTimer: ReturnType<typeof setInterval> | null = null

    const state = computed<MaintenanceState>(() => notice.value?.state ?? 'none')
    const isActive = computed(() => state.value === 'active')
    const isDismissed = computed(() => {
        const id = notice.value?.id
        if (!id) {
            return false
        }
        return dismissedIds.value.includes(id) || readDismissed(id)
    })
    const isVisible = computed(() =>
        state.value === 'active' || (state.value === 'upcoming' && !isDismissed.value))

    async function refresh() {
        const result = await maintenanceApi.getNotice(language.value)
        if (result != null) {
            notice.value = result
        }
    }

    function start(newLanguage: string) {
        const languageChanged = language.value !== newLanguage
        language.value = newLanguage

        if (pollTimer !== null) {
            if (languageChanged) {
                refresh()
            }
            return
        }

        refresh()
        pollTimer = setInterval(refresh, POLL_INTERVAL_MS)
        document.addEventListener('visibilitychange', onVisibilityChange)
    }

    function stop() {
        if (pollTimer !== null) {
            clearInterval(pollTimer)
            pollTimer = null
        }
        document.removeEventListener('visibilitychange', onVisibilityChange)
    }

    function dismiss() {
        const id = notice.value?.id
        if (!id || state.value !== 'upcoming') {
            return
        }
        dismissedIds.value = [...dismissedIds.value, id]
        writeDismissed(id)
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'visible') {
            refresh()
        }
    }

    function readDismissed(id: string): boolean {
        try {
            return localStorage.getItem(DISMISSED_KEY_PREFIX + id) !== null
        } catch {
            return false
        }
    }

    function writeDismissed(id: string) {
        try {
            localStorage.setItem(DISMISSED_KEY_PREFIX + id, '1')
        } catch {
            return
        }
    }

    return {
        notice,
        state,
        isActive,
        isDismissed,
        isVisible,
        refresh,
        start,
        stop,
        dismiss,
    }
})

export function formatMaintenanceDate(iso: string, locale: string): string {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso))
}

export function formatMaintenanceTime(iso: string, locale: string): string {
    return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(iso))
}

export function formatMaintenanceEnd(startIso: string | null, endIso: string, locale: string): string {
    const end = new Date(endIso)
    const sameDay = startIso != null && new Date(startIso).toDateString() === end.toDateString()

    return sameDay
        ? formatMaintenanceTime(endIso, locale)
        : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(end)
}
